import { ChatSync } from '@/app/sync/chats';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSearchTextByScope } from './reducer';
import { getLastRecentChat, selectFilteredRecentChatData } from './selectors';

/**
 * Custom hook for managing recent chats
 * @param {Object} options - Configuration options
 * @param {string} options.scope - Scope key for search (default: 'recentchat')
 * @returns {Object} Hook return object with chats data and control functions
 */
export const useRecentChats = (options = {}) => {
   const { scope = 'recentchat', fetchDelay = 0 } = options;
   const dispatch = useDispatch();
   const selector = useMemo(() => selectFilteredRecentChatData(scope), [scope]);
   const filteredChats = useSelector(selector);
   const searchText = useSelector(state => state.recentChatData?.searchTextByScope[scope] || '');
   const stateData = useSelector(state => state.recentChatData);
   const { loading, error, hasMore, unreadRecords, page } = stateData || {};
   /**
    * Load more chats (pagination)
    */
   const loadMore = useCallback(async () => {
      if (!hasMore || loading) return;
      const lastChat = getLastRecentChat();
      return await ChatSync.getRecentChats({
         messageTime: lastChat ? lastChat.messageTime || lastChat.timestamp : 0,
         limit: 20,
         direction: 'backward',
         fetchDelay,
      });
   }, [hasMore, loading, fetchDelay]);

   /**
    * Refresh chats (reload from first page)
    */
   const refreshChats = useCallback(async () => {
      return await ChatSync.getRecentChats({ messageTime: 0, direction: 'backward', fetchDelay });
   }, [fetchDelay]);

   /**
    * Update search text
    */
   const updateSearchText = useCallback(
      text => {
         dispatch(setSearchTextByScope({ scope, text }));
      },
      [scope, dispatch],
   );

   return {
      // Data
      chats: filteredChats,
      loading,
      error,
      hasMore,
      page,
      searchText,
      unreadRecords,
      // Actions
      getRecentChats: ChatSync.getRecentChats,
      loadMore,
      refreshChats,
      updateSearchText,
   };
};
